/**
 * MigrationDialog — the one-time v1.x → v2.0 upgrade prompt.
 *
 * Triggered from Dashboard.tsx when
 * `useMigrationStatus().data.legacy_agents_detected === true` and
 * `already_migrated === false`. Shows a preview of the legacy agents
 * parsed out of the old `config.yaml`, warns about brief downtime, and
 * offers a single "Import all agents" button that POSTs to
 * `/api/system/migrate-legacy` via `useMigrateLegacy`.
 *
 * UX rules:
 *   - ESC is disabled while the mutation is running so a user can't
 *     accidentally close the dialog mid-migration.
 *   - Once the import succeeds we flip into the "done" state and expose
 *     a single "Back to dashboard" button — the parent re-fetches the
 *     agents list via the `onSuccess` invalidation in `useMigrateLegacy`.
 *   - On error we keep the dialog open and offer Retry.
 */
import { useMemo } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, XCircle } from 'lucide-react';
import type { MigrationStatus } from '@/api/migration';
import { ApiError } from '@/api/client';
import { useMigrateLegacy } from '@/hooks/useMigration';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface MigrationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  status: MigrationStatus;
}

export function MigrationDialog({ open, onOpenChange, status }: MigrationDialogProps) {
  const migrate = useMigrateLegacy();
  const legacy = status.legacy_agents ?? [];
  const count = legacy.length;

  const isRunning = migrate.isPending;
  const isDone = migrate.isSuccess && !!migrate.data;

  const errorMsg = useMemo(() => {
    if (!migrate.isError) return null;
    const err = migrate.error;
    if (err instanceof ApiError) return err.detail ?? err.title;
    return (err as Error)?.message ?? 'Migration failed';
  }, [migrate.isError, migrate.error]);

  async function handleImport() {
    try {
      await migrate.mutateAsync();
    } catch {
      /* error state is surfaced via migrate.isError */
    }
  }

  function handleOpenChange(next: boolean) {
    // Guard: do not allow closing while the mutation is in flight.
    if (isRunning && !next) return;
    onOpenChange(next);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-xl"
        onEscapeKeyDown={(e) => {
          if (isRunning) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (isRunning) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isDone ? (
              <CheckCircle2 className="h-5 w-5 text-success" aria-hidden />
            ) : (
              <AlertTriangle className="h-5 w-5 text-warning" aria-hidden />
            )}
            {isDone ? 'Migration complete' : 'Legacy agents detected'}
          </DialogTitle>
          <DialogDescription>
            {isDone
              ? `${migrate.data?.imported ?? 0} agents imported. Your old config.yaml was archived to ${migrate.data?.archived_config ?? 'config.yaml.pre-v2'}.`
              : `We found ${count} agent${count === 1 ? '' : 's'} in your existing config.yaml. AgentHub v2.0 manages agents in the panel instead. Would you like to import them?`}
          </DialogDescription>
        </DialogHeader>

        {/* ----- body ----- */}
        {!isDone && (
          <div className="space-y-3">
            <div className="max-h-56 space-y-1.5 overflow-y-auto rounded-md border border-border p-2">
              {legacy.length === 0 ? (
                <p className="px-1.5 py-2 text-xs text-muted-foreground">
                  No legacy agents to list.
                </p>
              ) : (
                legacy.map((agent) => (
                  <div
                    key={agent.name}
                    className="rounded-sm border border-border/60 bg-muted/30 px-2 py-1.5 text-xs"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono font-semibold">{agent.name}</span>
                      <span className="text-muted-foreground">
                        {agent.channel_ids.length} channel
                        {agent.channel_ids.length === 1 ? '' : 's'} ·{' '}
                        {agent.user_ids.length} user
                        {agent.user_ids.length === 1 ? '' : 's'}
                      </span>
                    </div>
                    {agent.description && (
                      <div className="mt-0.5 truncate text-muted-foreground">
                        {agent.description}
                      </div>
                    )}
                    <div className="mt-0.5 font-mono text-[10px] text-muted-foreground">
                      token: {agent.discord_bot_token_masked}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning-foreground">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
              <p>
                Agents will briefly go offline during migration (~30 seconds per agent).
                Your conversations will be preserved.
              </p>
            </div>

            {isRunning && (
              <div
                className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs"
                aria-live="polite"
              >
                <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" aria-hidden />
                Migrating{' '}
                {legacy[0]?.name ? (
                  <span className="font-mono font-semibold">{legacy[0].name}</span>
                ) : (
                  'agents'
                )}
                …
              </div>
            )}

            {errorMsg && (
              <div
                className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive"
                role="alert"
              >
                <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                <span>{errorMsg}</span>
              </div>
            )}
          </div>
        )}

        {isDone && migrate.data && (
          <div className="space-y-2">
            {migrate.data.failed.length > 0 && (
              <div className="space-y-1 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs">
                <div className="font-semibold text-destructive">
                  {migrate.data.failed.length} agent
                  {migrate.data.failed.length === 1 ? '' : 's'} failed:
                </div>
                <ul className="list-disc pl-4 text-destructive">
                  {migrate.data.failed.map((f) => (
                    <li key={f.name}>
                      <span className="font-mono">{f.name}</span>: {f.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Your agents will reconnect momentarily. You can now manage them from the dashboard.
            </p>
          </div>
        )}

        {/* ----- footer ----- */}
        <DialogFooter>
          {isDone ? (
            <Button onClick={() => onOpenChange(false)}>Back to dashboard</Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isRunning}
              >
                Not now
              </Button>
              {migrate.isError ? (
                <Button onClick={handleImport} disabled={isRunning}>
                  <RefreshCw className="h-3.5 w-3.5" aria-hidden />
                  Retry import
                </Button>
              ) : (
                <Button onClick={handleImport} disabled={isRunning || legacy.length === 0}>
                  {isRunning ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                      Importing…
                    </>
                  ) : (
                    <>
                      Import all agents
                    </>
                  )}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
